import { FC, useMemo } from 'react';
import { QUERY_requestTeamMemberForOrder } from 'queries';
import { useSSFBehaviors } from 'hooks';
import { createGQLDataContext } from 'utils/createDataContext';
import { ISSFBehaviorsOptions } from 'hooks/useSSFBehaviors/useSSFBehaviors.types';
import { pageContextInitialState } from 'types/common.types';
import { ApolloClient, InMemoryCache } from '@apollo/client';
import { createApolloClient } from 'providers/ApolloProvider';
import { ILocationFilterProps } from 'components/LocationFilterInput/LocationFilterInput.types';
import { ParticipatingCommunitiesAttributes } from 'types/graphql';

export const RequestTeamMemberForOrderContext = createGQLDataContext({
  ...pageContextInitialState,
});

const config = ({
  startTime,
  endTime,
  breakTime,
  allowOvertime = false,
  location,
  byPosition = [],
  orderId,
  withParticipatingCommunities,
  client,
}: {
  orderId?: string;
  startTime?: string;
  endTime?: string;
  breakTime?: string;
  allowOvertime?: boolean;
  location: ILocationFilterProps['value'];
  byPosition?: number[];
  withParticipatingCommunities?: ParticipatingCommunitiesAttributes;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  client: ApolloClient<any>;
}): ISSFBehaviorsOptions => ({
  key: 'REQUEST_TEAM_MEMBER_FOR_ORDER',
  query: QUERY_requestTeamMemberForOrder,
  client,
  payloadTransformer: (variables) => {
    const locationId = Number(location.value as string);

    variables.withServiceableCommunity = locationId;
    variables.relevantForOrder = {
      ...(!!orderId
        ? { orderId }
        : {
            byFacilityId: locationId,
          }),
      byPositionId: byPosition[0],
      byStartTime: startTime,
      byEndTime: endTime,
      withBreakTime: breakTime,
      allowOvertime,
    };

    variables.byPosition = byPosition;

    if (withParticipatingCommunities) {
      variables.withParticipatingCommunities = withParticipatingCommunities;
    }

    return variables;
  },
  paginationType: 'InfiniteScroll',
  schema: {
    byMultipleFields: {
      initialValue: '',
      mapper: String,
    },
    withServiceableCommunity: {
      initialValue: undefined,
    },
    byPosition: {
      initialValue: [],
    },
    startTime: {
      initialValue: '',
    },
    endTime: {
      initialValue: '',
    },
    breakTime: {
      initialValue: '',
    },
    allowOvertime: {
      initialValue: false,
    },
    sort: {
      initialValue: [
        {
          field: 'firstName',
          direction: 'asc',
        },
      ],
    },
  },
});

export const RequestTeamMemberForOrderProvider: FC<{
  location: ILocationFilterProps['value'];
  orderId?: string;
  byPosition?: number[];
  withParticipatingCommunities?: ParticipatingCommunitiesAttributes;
  startTime?: string;
  endTime?: string;
  breakTime?: string;
  allowOvertime?: boolean;
}> = ({
  location,
  orderId,
  byPosition,
  withParticipatingCommunities,
  startTime,
  endTime,
  breakTime,
  allowOvertime,
  children,
}) => {
  const client = useMemo(
    () =>
      createApolloClient({
        cache: new InMemoryCache({
          typePolicies: {
            Query: {
              fields: {
                requestJanes: {
                  keyArgs: false,
                  merge(existing = { edges: [] }, incoming = { edges: [] }) {
                    const nextEdges = [...existing.edges];
                    if (incoming.edges?.length > 0)
                      incoming.edges.forEach((record: { node: { __ref?: string } }) => {
                        if (record && record.node?.__ref)
                          if (!nextEdges.some(({ node: __ref }) => record.node.__ref === __ref))
                            nextEdges.push(record);
                      });
                    return { ...existing, ...incoming, edges: nextEdges };
                  },
                },
              },
            },
          },
        }),
      }),
    [],
  );
  const configObject = useMemo(
    () =>
      config({
        orderId,
        location,
        byPosition,
        withParticipatingCommunities,
        startTime,
        endTime,
        breakTime,
        allowOvertime,
        client,
      }),
    [
      orderId,
      location,
      byPosition,
      withParticipatingCommunities,
      startTime,
      endTime,
      breakTime,
      allowOvertime,
      client,
    ],
  );
  const [values, useQueryOptions] = useSSFBehaviors(configObject);
  const value = useMemo(() => ({ values, ...useQueryOptions }), [useQueryOptions, values]);
  return (
    <RequestTeamMemberForOrderContext.Provider value={value}>
      {children}
    </RequestTeamMemberForOrderContext.Provider>
  );
};
